import { IntegrationType } from '@weave/schema-gen-ts/dist/schemas/syncapp/integrations-service/integration.pb';
import { CustomizationFlagTypes } from '@frontend/api-customization-flags';
import { PlgRetentionApi, PlgRetentionTypes } from '@frontend/api-plg-retention';
import { SchemaIntegrationsService } from '@frontend/schema';
import { shell } from '@frontend/shell-utils';
import { pendo, sentry } from '@frontend/tracking';
import { userIdentityChanged } from './app-lifecycle-subjects';

type IdentifiedUser = PlgRetentionTypes.CustomerInfo & {
  visitorId: string;
  visitorRole: string;
  accountId: string;
  visitorUsername: string;
  browserWidth: number;
  browserHeight: number;
  pmsList?: string;
  customizationFlagsActive: string[];
  customizationFlagsHidden: string[];
  customizationFlagsTrial: string[];
};

let currentIdentifiedUser: IdentifiedUser | null = null;

const visitorApp = 'frontend/weave';

function toggleAdaEmbed() {
  window?.adaEmbed?.toggle();
}
/**
 * trigger ada chat bot from inside pendo resource center
 */
function addAdaTrigger() {
  const observer = new MutationObserver(() => {
    const pendoGuidesList = document.getElementById(pendo.domSelectors.guideListId);
    const liElements = pendoGuidesList?.getElementsByTagName('li');
    if (liElements) {
      Array.from(liElements).forEach((li) => {
        if (li?.textContent?.includes('Chat With Us')) {
          li.addEventListener('click', toggleAdaEmbed);
        }
      });
    }
  });
  observer.observe(document.body, {
    childList: true,
    subtree: true,
  });
}

export const addPendoScript = () => {
  pendo.initWithCallback(import.meta.env.VITE_WEAVE_PENDO_API_KEY, () => {
    pendo.initialize({
      visitorApp,
      visitorShellVersion: shell.isShell ? shell.version : undefined,
      visitorShellFlavor: shell.isShell ? shell.flavor : undefined,
      visitorIsShell: shell.isShell ?? false,
      browserWidth: window.innerWidth,
      browserHeight: window.innerHeight,
    });

    addAdaTrigger();

    if (currentIdentifiedUser) {
      pendo.identify({
        visitorApp,
        ...currentIdentifiedUser,
      });
    }
  });
};

const groupCustomizationFlagsByState = (flags: CustomizationFlagTypes.CustomizationFlag[] = []) => {
  type GroupedCustomizationFlags = { active: string[]; hide: string[]; trial: string[] };

  const grouped = flags.reduce<GroupedCustomizationFlags>(
    (acc, flag) => {
      switch (flag.state) {
        case 'ACTIVE': {
          return {
            ...acc,
            active: [...acc.active, flag.featureEnum!],
          };
        }
        case 'HIDE': {
          return {
            ...acc,
            hide: [...acc.hide, flag.featureEnum!],
          };
        }
        case 'TRIAL': {
          return {
            ...acc,
            trial: [...acc.trial, flag.featureEnum!],
          };
        }
        default:
          return acc;
      }
    },
    { active: [], hide: [], trial: [] }
  );

  return grouped;
};

userIdentityChanged.addListener(async ({ user, decodedWeaveToken, isWeaveUser, locationId, customizationFlags }) => {
  const groupedFlags = groupCustomizationFlagsByState(customizationFlags);

  currentIdentifiedUser = {
    visitorId: user?.userID || 'unknown',
    visitorRole: decodedWeaveToken?.type || 'unknown',
    accountId: isWeaveUser ? 'weave' : locationId,
    visitorUsername: user?.username || 'unknown',
    browserWidth: window.innerWidth,
    browserHeight: window.innerHeight,
    customizationFlagsActive: groupedFlags.active,
    customizationFlagsHidden: groupedFlags.hide,
    customizationFlagsTrial: groupedFlags.trial,
  };

  try {
    const customerInfo = await PlgRetentionApi.getCustomerInfo([locationId]);
    currentIdentifiedUser = { ...currentIdentifiedUser, ...customerInfo };
  } catch (error) {
    console.error(error);
    sentry.log({ category: 'onboarding', message: 'error received in getCustomerInfo API' });
  }

  try {
    const integrationList = await SchemaIntegrationsService.ListDataSourcesByLocationID({ locationId: locationId });
    currentIdentifiedUser.pmsList = integrationList.dataSources?.reduce<string>((acc, dataSource) => {
      if (
        dataSource.integration?.type === IntegrationType.CLOUD ||
        dataSource.integration?.type === IntegrationType.ON_PREM
      ) {
        acc = acc.trim() + ` ${dataSource?.integration?.displayName ?? ''}`;
      }
      return acc;
    }, '');
  } catch (error) {
    console.error(error);
    sentry.log({ category: 'onboarding', message: 'error received in listDataSources API' });
  }

  sentry.log({
    category: 'onboarding',
    message: 'userIdentityChanged listener called',
    data: {
      customerInfo: currentIdentifiedUser,
    },
  });
  pendo.identify({
    visitorApp,
    ...currentIdentifiedUser,
  });
});
